// src/components/BlogShowcase.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchBlogPosts } from '../services/apiService';

// Helper to truncate text
const truncateText = (html, wordLimit) => {
  if (!html) return '';
  const text = html.replace(/<[^>]*>/g, '');
  const words = text.split(/\s+/);
  return words.length <= wordLimit ? text : words.slice(0, wordLimit).join(' ') + '…';
};

const BlogShowcase = ({ title, subtitle, category, count = 7, showViewAllLink = true }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getPosts = async () => {
      setLoading(true);
      try {
        // ✅ FIX: Destructure the 'posts' array from the response object.
        const { posts: allPosts } = await fetchBlogPosts({ perPage: 100 }); // Fetch all to allow for filtering
        
        let filteredPosts = allPosts;
        if (category) {
          filteredPosts = allPosts.filter(p => p.categories.some(c => c.name.toLowerCase().replace(/\s+/g, '-') === category));
        }
        // ✅ FIX: Use the correctly destructured 'filteredPosts' array here.
        setPosts(filteredPosts.slice(0, count)); 
      } catch (err) {
        setError('Failed to fetch blog posts.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    getPosts();
  }, [category, count]);

  if (loading) return <div className="text-center py-24 text-dark-text-primary">Loading Showcase...</div>;
  if (error || posts.length === 0) return null; // Don't render if error or no posts

  const featuredPost = posts[0];
  const secondaryPosts = posts.slice(1);

  return (
    <section className="bg-dark-bg py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">{title}</h2>
          <p className="mt-4 text-lg text-dark-text-secondary">{subtitle}</p>
        </div>
        <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-12 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:gap-16">
          {/* Featured Post */}
          <Link to={`/blog/${featuredPost.slug}`} className="group flex flex-col">
            <div className="aspect-square overflow-hidden rounded-2xl border border-white/10">
              <img src={featuredPost.featuredMedia?.source_url} alt={featuredPost.title} className="h-full w-full object-cover transition-transform duration-500 ease-in-out group-hover:scale-105" />
            </div>
            <div className="mt-6">
              <p className="text-sm font-medium text-dark-accent-start">{featuredPost.categories?.[0]?.name}</p>
              <h3 className="mt-2 text-2xl font-bold text-dark-text-primary group-hover:text-white" dangerouslySetInnerHTML={{ __html: featuredPost.title }} />
              <p className="mt-4 text-base text-dark-text-secondary" dangerouslySetInnerHTML={{ __html: truncateText(featuredPost.excerpt, 25) }}/>
              <div className="mt-4 flex items-center gap-x-4">
                {featuredPost.author.avatarUrl && <img src={featuredPost.author.avatarUrl} alt="" className="h-10 w-10 rounded-full bg-gray-800" />}
                <div>
                  <p className="font-semibold text-dark-text-primary">{featuredPost.author.name}</p>
                  <p className="text-sm text-dark-text-secondary/70">{new Date(featuredPost.dateTime).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          </Link>
          {/* Secondary Posts */}
          <div className="space-y-12">
            {secondaryPosts.map(post => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="group flex items-center gap-x-6">
                <div className="h-24 w-24 flex-none overflow-hidden rounded-xl border border-white/10">
                  <img src={post.featuredMedia?.source_url} alt="" className="h-full w-full object-cover transition-transform duration-500 ease-in-out group-hover:scale-105" />
                </div>
                <div>
                  <p className="text-sm font-medium text-dark-accent-start">{post.categories?.[0]?.name}</p>
                  <h4 className="mt-1 text-base font-semibold text-dark-text-primary group-hover:text-white" dangerouslySetInnerHTML={{ __html: post.title }} />
                  <p className="text-xs text-dark-text-secondary/70 mt-1">{new Date(post.dateTime).toLocaleDateString()}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
        {showViewAllLink && (
            <div className="text-center mt-16">
                <Link to="/all-articles" className="text-sm font-semibold text-dark-text-primary hover:text-white">
                    View all posts &rarr;
                </Link>
            </div>
        )}
      </div>
    </section>
  );
};

export default BlogShowcase;